import _ from 'lodash'
import Guard from 'arg-guard'

import Casing from './helpers/casing'
import Logger from './logger'
import { Array } from 'core-js'

export default {

    /**
     * Binds server validation results from an error.
     * The elements used to source the data and show validation errors should have refs or name tags with the property name in.
     *
     * @param {Error} error The source error.
     * @param {Object} refs If using Vue refs the refs object must be passed in to the method.
     * @param {string} [validatorSuffix='.validator'] An optional override to the validator suffix.
     * @returns {Object} A Results set to help debugging setting up the right binding tags and all of the errors in an array.
     */
    bindResults(error, refs, validatorSuffix = '.validator') {
        Guard.throwIf(error, 'error')
        Logger.logObject(error, 'Binding Errors', 'Server validator')

        /**
         * @type {Array<Object>}
         */
        const results = []

        /**
         * @type {Array<string>}
         */
        const allErrors = []

        /**
         * Changes the output for certain errors.
         * @param {Array} errors An array of errors.
         */
        const rewordErrors = (errors) => {
            return errors.map(err => {
                if (err.message.includes('Name ') && err.message.includes(' is already taken')) {
                    const email = err.message.replace('Name ', '').replace(' is already taken.', '')
                    err.message = `An account has already been registered to email address '${ Casing.lowerCase(email) }.'`
                }
                if (err.message.includes('Passwords must have at least one lowercase'))
                    err.message = `Passwords must contain at least one lowercase letter`
                if (err.message.includes('Passwords must have at least one uppercase'))
                    err.message = `Passwords must contain at least one uppercase letter`
                if (err.message.includes('Passwords must have at least one digit'))
                    err.message = `Passwords must contain at least one number`

                return err
            })
        }

        /**
         * Processes the errors provided from an array in a response.
         * @param {Array<ServerValidatorErrorObject>} errorsArray An array of errors.
         */
        const processErrorArrayData = (errorsArray) => {
            let eithered = false
            Logger.logObject(errorsArray, 'Processing errors array', 'Server validator')

            const errs = rewordErrors(errorsArray)

            for (let idx = 0; idx < errs.length; idx++) {
                const err = errs[idx]

                let linkedEl = err.reference && refs && refs[err.reference]
                if (!linkedEl) linkedEl = document.getElementsByName(err.reference)[0]
                const linkedValidator = linkedEl && document.getElementsByName(`${ err.reference }${ validatorSuffix }`)[0]

                results.push({
                    property: err.reference,
                    errors: err.message,
                    element: linkedEl,
                    validator: linkedValidator,
                })

                if (linkedValidator) {
                    linkedValidator.innerText = err && err.message
                } else {
                    if (err.message && err.message.includes('Either')) {
                        if (!eithered) {
                            eithered = true
                            allErrors.push(err.message)
                        }
                    } else {
                        allErrors.push(err.message)
                    }
                }
            }
        }

        /**
         * Processes an error object provided in a response.
         * @param {ServerValidatorErrorObject} errorObject An object representing an error.
         */
        const processErrorObject = (errorObject) => {
            Logger.logObject(errorObject, 'Processing error object', 'Server validator')

            if (errorObject.reference)
                allErrors.push(`${ errorObject.reference } (${ errorObject.message })`)
            else
                allErrors.push(errorObject.message)
        }

        /**
         * Processes the errors provided from an object in a response.
         * @param {Object} validation An object containing errors.
         */
        const processErrorObjectData = (validation) => {
            let eithered = false
            Logger.logObject(validation, 'Processing errors object', 'Server validator')

            for (const prop in validation) {
                const errorMessage = _.uniq(validation[prop]).join(`, `).replace(`.`, ``)
                let linkedEl = refs && refs[prop]
                if (!linkedEl) linkedEl = document.getElementsByName(prop)[0]
                const linkedValidator = document.getElementsByName(`${ prop }${ validatorSuffix }`)[0]

                results.push({
                    property: prop,
                    errors: errorMessage,
                    element: linkedEl,
                    validator: linkedValidator,
                })

                if (linkedValidator) {
                    linkedValidator.innerText = errorMessage
                } else {
                    if (errorMessage.includes('Either')) {
                        if (!eithered) {
                            eithered = true
                            allErrors.push(errorMessage)
                        }
                    } else {
                        allErrors.push(errorMessage)
                    }
                }
            }

            if (validation.map) {
                const errs = rewordErrors(validation)
                const errorMessage = _.uniq(errs.map(err => err.message)).join(', ')
                allErrors.push(errorMessage)
            }
        }

        /**
         * Processes an error string provided in a response.
         * @param {string} errorMessage An error message.
         */
        const processErrorString = (errorMessage) => {
            Logger.logObject(errorMessage, 'Processing error message', 'Server validator')
            allErrors.push(errorMessage)
        }

        if (error.response && error.response.data) {

            if (error.response.data.errors) // Complex validation error object
                processErrorObjectData(error.response.data.errors)
            else if (Array.isArray(error.response.data) && error.response.data.length) // Array of errors
                processErrorArrayData(error.response.data)
            else if (error.response.data.message) // Single error object
                processErrorObject(error.response.data)
            else // Simple error message string
                processErrorString(error.response.data)
        }

        const retVal = {
            results,
            allErrors,
            rootError: error,
            rootResponse: error.response,
        }

        Logger.logObject(retVal, 'Error binding result', 'Server Validator')
        return retVal
    },

    /**
     * Clears the validation errors.
     *
     * @param {Object} refs The Vue component references.
     * @param {string} [validatorSuffix='.validator'] The suffix to the item.
     */
    clear(refs, validatorSuffix = '.validator') {
        Guard.throwIf(refs, 'refs')

        for (const item in refs) {
            const ref = String(item)
            const linkedValidator = document.getElementsByName(`${ ref }${ validatorSuffix }`)[0]

            if (linkedValidator)
                linkedValidator.innerText = ''
        }
    },

    /**
     * Formats the master error message from a validation result.
     *
     * @param {Object} validationResult The validation results from a previous call to [bindResults()]
     * @param {string} [messageRoot='An error occurred:'] The base error message.
     * @returns {string} A formatted error message.
     */
    formatErrorMessageFromResults(validationResult, messageRoot = 'An error occurred') {
        return validationResult.rootError.message.includes('status code 400') || validationResult.rootError.message.includes('request is invalid')
            ? validationResult.allErrors && validationResult.allErrors.length
                ? `${ messageRoot }: ${ validationResult.allErrors.join(', ') }`
                : messageRoot
            : validationResult.allErrors && validationResult.allErrors.length
                ? `${ messageRoot }: ${ validationResult.rootError.message } [${ validationResult.allErrors.join(', ') }]`
                : `${ messageRoot }: ${ validationResult.rootError.message }`
    },
}
